import { Check, X } from "lucide-react";

/**
 * Password policy used across signup, reset and change-password flows.
 * Mirrored in `backend/core/security.py::validate_password_policy`.
 */
export const PASSWORD_RULES = [
  {
    id: "length",
    label: "8–30 characters",
    test: (v) => v.length >= 8 && v.length <= 30,
  },
  {
    id: "upper",
    label: "At least one uppercase letter",
    test: (v) => /[A-Z]/.test(v),
  },
  {
    id: "lower",
    label: "At least one lowercase letter",
    test: (v) => /[a-z]/.test(v),
  },
  {
    id: "number",
    label: "At least one number",
    test: (v) => /[0-9]/.test(v),
  },
  {
    id: "special",
    label: "At least one special character",
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];

export function isPasswordValid(value = "") {
  return PASSWORD_RULES.every((r) => r.test(value));
}

/**
 * Visual checklist that updates live as the user types.
 *
 * Variants:
 *   - "tooltip" (default): floating callout, used inline below a password field
 *   - "popover":           absolutely positioned to the right of the field on
 *                          wide screens, used in the auth pages
 */
export default function PasswordRequirements({
  value = "",
  visible = true,
  variant = "tooltip",
  className = "",
}) {
  if (!visible) return null;

  const wrapperBase =
    variant === "popover"
      ? "rounded-2xl bg-white border border-plum/10 shadow-soft-md p-4 " +
        "lg:absolute lg:left-full lg:top-0 lg:ml-4 lg:w-72 lg:z-20"
      : "rounded-2xl bg-cream/70 border border-plum/10 p-3";

  return (
    <div
      className={wrapperBase + " " + className}
      role="status"
      aria-live="polite"
      data-testid="password-requirements"
    >
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-ink mb-2">
        Password should contain
      </p>
      <ul className="space-y-1.5">
        {PASSWORD_RULES.map((rule) => {
          const ok = rule.test(value);
          return (
            <li
              key={rule.id}
              className="flex items-center gap-2 text-xs leading-snug transition-colors"
              data-testid={`pwd-rule-${rule.id}`}
              data-met={ok ? "true" : "false"}
            >
              <span
                className={
                  "shrink-0 w-4 h-4 rounded-full flex items-center justify-center " +
                  (ok ? "bg-mint text-white" : "bg-plum/10 text-inkmid")
                }
              >
                {ok ? <Check className="w-3 h-3" strokeWidth={3} /> : <X className="w-2.5 h-2.5" strokeWidth={3} />}
              </span>
              <span className={ok ? "text-mint font-semibold" : "text-inkmid"}>{rule.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
